
import Vue from 'vue';
import { message } from 'ant-design-vue';
import { getEducationList, getWorkDetail, saveApi, updateApi } from '../api';
import AiSupplementFields from '../components-pc/AiSupplementFields.vue';
import HomeFooter from '../components-pc/HomeFooter.vue';
import PcPageLoading from '../components-pc/PcPageLoading.vue';
import { uploadFileToOss } from '../components-pc/ossUpload';

const baseUrl = 'cqcc';
const MB = 1024 * 1024;
const DEFAULT_EDUCATION_OPTIONS: EducationOption[] = [
  { label: '专科', value: '专科' },
  { label: '本科', value: '本科' },
  { label: '硕士研究生', value: '硕士研究生' },
  { label: '博士研究生', value: '博士研究生' },
];

interface ReportForm {
  workName: string;
  authorName: string;
  teacherName: string;
  schoolName: string;
  grade: string;
  education: string;
  contact: string;
  intro: string;
  ai: AiSupplementForm;
}

interface AiSupplementForm {
  isAi: string;
  tools: string;
  cover: string;
  overUrl: string;
  contents: string;
}

interface ImageSize {
  width: number;
  height: number;
}

interface ReportFile extends ImageSize {
  name: string;
  file: File;
  url: string;
  uploadUrl?: string;
}

interface WorkRow {
  id: string;
  file: ReportFile | null;
  description: string;
  errors: string[];
  validating: boolean;
}

interface EducationOption {
  label: string;
  value: string;
}

interface AiSupplementInstance extends Vue {
  validate(): boolean;
  uploadCoverToOss(): Promise<string>;
  clear(): void;
  setInitialCover(url: string): void;
}

const createDefaultAiSupplementForm = (): AiSupplementForm => ({
  isAi: '1',
  tools: '',
  cover: '',
  overUrl: '',
  contents: '',
});

export default Vue.extend({
  name: 'ReportSignupPc',
  components: {
    AiSupplementFields,
    HomeFooter,
    PcPageLoading,
  },
  data() {
    return {
      pageLoading: false,
      submitting: false,
      aiCoverValidating: false,
      educationOptions: [] as EducationOption[],
      form: {
        workName: '',
        authorName: '',
        teacherName: '',
        schoolName: '',
        grade: '',
        education: '',
        contact: '',
        intro: '',
        ai: createDefaultAiSupplementForm(),
      } as ReportForm,
      workRows: [] as WorkRow[],
    };
  },
  computed: {
    hasValidatingRow(): boolean {
      return this.workRows.some((item: WorkRow) => item.validating);
    },
  },
  created() {
    this.workRows = [this.createWorkRow()];
  },
  mounted() {
    this.initPageData();
  },
  beforeDestroy() {
    this.workRows.forEach((item: WorkRow) => this.revokeRowFile(item));
  },
  methods: {
    async initPageData() {
      this.pageLoading = true;
      try {
        await Promise.all([
          this.fetchEducationOptions(),
          this.fetchWorkDetail(),
        ]);
      } finally {
        this.pageLoading = false;
      }
    },
    goSignup() {
      this.$router.push(`/${baseUrl}/signup`).catch((err: Error) => err);
    },
    getEditId(): string {
      const id = this.$route.query.id;
      return Array.isArray(id) ? String(id[0] || '') : String(id || '');
    },
    getDetailPayload(res: any): any {
      return (
        (res && res.data && res.data.data) || (res && res.data) || res || {}
      );
    },
    getDefaultForm(): ReportForm {
      return {
        workName: '',
        authorName: '',
        teacherName: '',
        schoolName: '',
        grade: '',
        education: '',
        contact: '',
        intro: '',
        ai: this.createAiSupplementForm(),
      };
    },
    normalizeAiFlag(value: any): string {
      return Number(value) === 1 ? '1' : '0';
    },
    getDetailAiFlag(detail: any): any {
      if (
        detail &&
        detail.is_ai !== undefined &&
        detail.is_ai !== null &&
        detail.is_ai !== ''
      ) {
        return detail.is_ai;
      }
      return detail && detail.isAi;
    },
    createAiSupplementForm(detail?: any): AiSupplementForm {
      const rawFlag = detail ? this.getDetailAiFlag(detail) : '1';
      return {
        isAi:
          rawFlag === undefined || rawFlag === null || rawFlag === ''
            ? '1'
            : this.normalizeAiFlag(rawFlag),
        tools: (detail && detail.tools) || '',
        cover: (detail && detail.cover) || '',
        overUrl: (detail && (detail.over_url || detail.overUrl)) || '',
        contents: (detail && detail.contents) || '',
      };
    },
    getAiSubmitParams(coverUrl: string) {
      const aiForm = this.form.ai;
      const isAi = aiForm.isAi === '1';
      return {
        is_ai: isAi ? 1 : 0,
        tools: isAi ? aiForm.tools : '',
        cover: isAi ? coverUrl : '',
        over_url: isAi ? aiForm.overUrl : '',
        contents: isAi ? aiForm.contents : '',
      };
    },
    async fetchWorkDetail() {
      const id = this.getEditId();
      if (!id) {
        return;
      }

      try {
        const res: any = await getWorkDetail({ id });
        const detail = this.getDetailPayload(res);
        this.form = {
          workName: detail.title || '',
          authorName: detail.name || '',
          teacherName: detail.teacher || '',
          schoolName: detail.school || '',
          grade: detail.class || '',
          education: detail.education || '',
          contact:
            detail.contact ||
            detail.contact_way ||
            detail.contactWay ||
            detail.phone ||
            detail.mobile ||
            '',
          intro: detail.introduction || '',
          ai: this.createAiSupplementForm(detail),
        };
        const rows = this.parseReportRows(detail.files);
        if (rows.length) {
          this.workRows.forEach((item: WorkRow) => this.revokeRowFile(item));
          this.workRows = rows;
        }
        this.$nextTick(() => {
          const aiSupplement = this
            .$refs.aiSupplement as AiSupplementInstance;
          if (aiSupplement) {
            aiSupplement.setInitialCover(this.form.ai.cover);
          }
        });
      } catch (err) {
        console.error('图文报道作品详情获取失败：', err);
        message.error('作品详情获取失败，请稍后重试');
      }
    },
    parseReportRows(value: any): WorkRow[] {
      let list: any[] = [];
      if (Array.isArray(value)) {
        list = value;
      } else if (value) {
        try {
          const parsed = JSON.parse(value);
          list = Array.isArray(parsed) ? parsed : [];
        } catch (err) {
          list = [];
        }
      }

      return list
        .map((item, index) => {
          const imageUrl =
            typeof item === 'string'
              ? item
              : item.imageUrl || item.image_url || item.url || '';
          if (!imageUrl) {
            return null;
          }
          return {
            id: `${Date.now()}-${Math.random()}`,
            file: this.createRemoteReportFile(imageUrl, index),
            description:
              typeof item === 'string'
                ? ''
                : String(item.description || item.explanation || ''),
            errors: [],
            validating: false,
          };
        })
        .filter(Boolean) as WorkRow[];
    },
    createRemoteReportFile(url: string, index: number): ReportFile {
      return {
        name: this.getFileNameFromUrl(url) || `已上传图片${index + 1}`,
        file: null as any,
        url,
        uploadUrl: url,
        width: 0,
        height: 0,
      };
    },
    getFileNameFromUrl(url: string): string {
      const path = url.split('?')[0].split('#')[0];
      try {
        return decodeURIComponent(path.split('/').pop() || '');
      } catch (err) {
        return path.split('/').pop() || '';
      }
    },
    resetForm() {
      this.form = this.getDefaultForm();
      this.workRows.forEach((item: WorkRow) => this.revokeRowFile(item));
      this.workRows = [this.createWorkRow()];
      const aiSupplement = this.$refs.aiSupplement as AiSupplementInstance;
      if (aiSupplement) {
        aiSupplement.clear();
      }
    },
    createWorkRow(): WorkRow {
      return {
        id: `${Date.now()}-${Math.random()}`,
        file: null,
        description: '',
        errors: [],
        validating: false,
      };
    },
    normalizeEducationOption(item: any): EducationOption | null {
      if (typeof item === 'string' || typeof item === 'number') {
        const value = String(item);
        return { label: value, value };
      }

      if (!item || typeof item !== 'object') {
        return null;
      }

      const label =
        item.label ||
        item.name ||
        item.title ||
        item.education ||
        item.text ||
        item.value;
      const value = item.value || item.id || item.key || label;

      if (!label || value === undefined || value === null) {
        return null;
      }

      return {
        label: String(label),
        value: String(value),
      };
    },
    getEducationPayload(res: any): any[] {
      if (Array.isArray(res)) {
        return res;
      }

      if (Array.isArray(res && res.data)) {
        return res.data;
      }

      if (Array.isArray(res && res.list)) {
        return res.list;
      }

      if (Array.isArray(res && res.data && res.data.list)) {
        return res.data.list;
      }

      return [];
    },
    async fetchEducationOptions() {
      try {
        const res: any = await getEducationList();
        const options = this.getEducationPayload(res)
          .map((item) => this.normalizeEducationOption(item))
          .filter(Boolean) as EducationOption[];

        this.educationOptions = options.length
          ? options
          : DEFAULT_EDUCATION_OPTIONS;
      } catch (err) {
        console.error('学历列表获取失败：', err);
        this.educationOptions = DEFAULT_EDUCATION_OPTIONS;
        message.error('学历列表获取失败，请稍后重试');
      }
    },
    isSaveSuccess(res: any): boolean {
      return Boolean(
        res &&
          (res.code === 200 ||
            res.status === 200 ||
            res.status === 1 ||
            res.success === true),
      );
    },
    getResponseMessage(res: any, fallback: string): string {
      return (res && (res.msg || res.message)) || fallback;
    },
    addWorkRow() {
      if (this.submitting) {
        return;
      }
      this.workRows.push(this.createWorkRow());
    },
    removeWorkRow(index: number) {
      if (this.submitting || index < 1) {
        return;
      }
      const removed = this.workRows.splice(index, 1)[0];
      if (removed) {
        this.revokeRowFile(removed);
      }
    },
    getFileInput(item: WorkRow): HTMLInputElement | null {
      const ref = this.$refs[`reportInput-${item.id}`] as
        | HTMLInputElement
        | HTMLInputElement[]
        | undefined;
      return Array.isArray(ref) ? ref[0] : ref || null;
    },
    openFilePicker(item: WorkRow) {
      if (this.submitting || item.validating) {
        return;
      }
      const input = this.getFileInput(item);
      if (input) {
        input.click();
      }
    },
    handleFileChange(event: Event, item: WorkRow) {
      const input = event.target as HTMLInputElement;
      this.validateFiles(input.files, item);
      input.value = '';
    },
    handleDrop(event: DragEvent, item: WorkRow) {
      if (this.submitting || item.validating) {
        return;
      }
      this.validateFiles(
        event.dataTransfer ? event.dataTransfer.files : null,
        item,
      );
    },
    async validateFiles(fileList: FileList | null, item: WorkRow) {
      if (!fileList || !fileList.length) {
        return;
      }
      if (fileList.length > 1) {
        item.errors = ['每行作品最多上传1张图片'];
        message.error('每行作品最多上传1张图片');
        return;
      }

      item.validating = true;
      item.errors = [];

      try {
        const reportFile = await this.validateReportFile(fileList[0]);
        this.revokeRowFile(item);
        item.file = reportFile;
        message.success('已添加图文报道作品');
      } catch (err) {
        item.errors = [err instanceof Error ? err.message : String(err)];
        message.error('图片不符合上传要求');
      } finally {
        item.validating = false;
      }
    },
    async validateReportFile(file: File): Promise<ReportFile> {
      if (
        !/\.(jpe?g|png)$/i.test(file.name) ||
        (file.type && ['image/jpeg', 'image/png'].indexOf(file.type) === -1)
      ) {
        throw new Error('仅支持JPG、PNG格式');
      }
      if (file.size > 10 * MB) {
        throw new Error('图片大小不能超过10MB');
      }

      const imageSize = await this.getImageSize(file);
      const url = URL.createObjectURL(file);
      return {
        name: file.name,
        file,
        url,
        width: imageSize.width,
        height: imageSize.height,
      };
    },
    getImageSize(file: File): Promise<ImageSize> {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const image = new Image();

        image.onload = () => {
          URL.revokeObjectURL(url);
          resolve({
            width: image.naturalWidth || image.width,
            height: image.naturalHeight || image.height,
          });
        };
        image.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('图片读取失败'));
        };
        image.src = url;
      });
    },
    revokeRowFile(item: WorkRow) {
      if (item.file) {
        URL.revokeObjectURL(item.file.url);
      }
    },
    async uploadReportFile(file: ReportFile): Promise<string> {
      if (file.uploadUrl) {
        return file.uploadUrl;
      }

      const uploadUrl = await uploadFileToOss(file);
      this.$set(file, 'uploadUrl', uploadUrl);
      return uploadUrl;
    },
    validateForm(): boolean {
      const requiredFields: Array<{ key: keyof ReportForm; label: string }> = [
        { key: 'workName', label: '作品名称' },
        { key: 'authorName', label: '作者名字' },
        { key: 'teacherName', label: '指导教师' },
        { key: 'schoolName', label: '所属院校' },
        { key: 'grade', label: '年级' },
        { key: 'education', label: '学历' },
        { key: 'contact', label: '联系方式' },
        { key: 'intro', label: '作品简介' },
      ];
      const emptyField = requiredFields.find((item) => !this.form[item.key]);

      if (emptyField) {
        message.error(`请填写${emptyField.label}`);
        return false;
      }

      const emptyWorkIndex = this.workRows.findIndex(
        (item: WorkRow) => !item.file,
      );
      if (emptyWorkIndex > -1) {
        message.error(`请上传第${emptyWorkIndex + 1}行作品`);
        return false;
      }

      const emptyDescIndex = this.workRows.findIndex(
        (item: WorkRow) => !item.description,
      );
      if (emptyDescIndex > -1) {
        message.error(`请填写第${emptyDescIndex + 1}行作品说明`);
        return false;
      }

      const aiSupplement = this.$refs.aiSupplement as AiSupplementInstance;
      if (aiSupplement && !aiSupplement.validate()) {
        return false;
      }

      return true;
    },
    async submitForm() {
      if (
        this.submitting ||
        this.hasValidatingRow ||
        this.aiCoverValidating ||
        !this.validateForm()
      ) {
        return;
      }

      const aiSupplement = this.$refs.aiSupplement as AiSupplementInstance;
      const hideLoading = message.loading('作品上传中，请稍候...', 0);
      this.submitting = true;

      try {
        const [files, aiCoverUrl] = await Promise.all([
          Promise.all(
            this.workRows.map(async (item: WorkRow) => ({
              imageUrl: await this.uploadReportFile(item.file as ReportFile),
              description: item.description,
            })),
          ),
          aiSupplement ? aiSupplement.uploadCoverToOss() : Promise.resolve(''),
        ]);
        const submitParams = {
          workType: 4,
          title: this.form.workName,
          name: this.form.authorName,
          school: this.form.schoolName,
          introduction: this.form.intro,
          files: JSON.stringify(files),
          teacher: this.form.teacherName,
          class: this.form.grade,
          education: this.form.education,
          contact: this.form.contact,
          explanation: '',
          exp_url: '',
          ...this.getAiSubmitParams(aiCoverUrl),
        };

        const id = this.getEditId();
        const res: any = id
          ? await updateApi({ ...submitParams, id })
          : await saveApi(submitParams);
        if (!this.isSaveSuccess(res)) {
          message.error(this.getResponseMessage(res, '提交失败，请稍后重试'));
          return;
        }

        message.success(
          this.getResponseMessage(res, id ? '修改成功' : '提交成功'),
        );
        this.resetForm();
        this.$router.back();
      } catch (err) {
        console.error('图文报道作品提交失败：', err);
        message.error('提交失败，请稍后重试');
      } finally {
        hideLoading();
        this.submitting = false;
      }
    },
  },
});
